import React, { useState, useEffect } from "react";
import TrainingAPI from "../services/TrainingAPI";
import Quiz from "./Quiz";
import LoadingSpinner from "../common/LoadingSpinner";
import "./css/Training.css";
import "./css/TrainingDetail.css";

/**
 * Formats a category string to display-ready format.
 * Converts hyphenated names like "social-engineering" to "Social Engineering".
 */
const formatCategoryForDisplay = (category) => {
  if (!category) return "General";
  if (category.includes(" ")) return category;
  return category
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * TrainingDetail - Displays a single training with reading content and quiz
 *
 * Features:
 * - Reading material display (HTML content)
 * - "Mark Complete" button for trainings without quizzes
 * - Quiz integration for trainings with quizzes (completion via passing)
 * - Certificate display on completion
 */
const TrainingDetail = ({
  trainingUuid,
  sessionId: initialSessionId,
  onBack,
  onComplete,
  isMobile = false,
}) => {
  const [training, setTraining] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);

  useEffect(() => {
    loadTraining();
  }, [trainingUuid]);

  const loadTraining = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get training details
      const trainingRes = await TrainingAPI.getTrainingByUuid(trainingUuid);
      if (trainingRes.success) {
        setTraining(trainingRes.training);

        // Start or resume session
        const sessionRes = await TrainingAPI.startTraining(trainingUuid);
        if (sessionRes.success) {
          setSession(sessionRes.session);
        }
      }
    } catch (err) {
      console.error("[TrainingDetail] Error loading training:", err);
      setError("Failed to load training. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!session) return;

    try {
      setMarkingComplete(true);
      const result = await TrainingAPI.markComplete({
        sessionId: session.sessionId,
      });

      if (result.success) {
        setSession(result.session);
        if (onComplete) {
          onComplete({ completed: true, passed: true });
        }
      }
    } catch (err) {
      console.error("[TrainingDetail] Error marking complete:", err);
      setError(err.message || "Failed to mark training as complete.");
    } finally {
      setMarkingComplete(false);
    }
  };

  const handleStartQuiz = async () => {
    setShowQuiz(true);
  };

  const handleQuizComplete = async (result) => {
    if (result.passed) {
      // Refresh session to get updated status
      const sessionRes = await TrainingAPI.startTraining(trainingUuid);
      if (sessionRes.success) {
        setSession(sessionRes.session);
      }

      if (onComplete) {
        onComplete(result);
      }
    }
    setShowQuiz(false);
  };

  if (loading) {
    return (
      <div className="training-container">
        <LoadingSpinner message="Loading training..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="training-container">
        <div className="training-error">
          <div className="training-error-icon">!</div>
          <p className="training-error-message">{error}</p>
          <button className="btn btn-primary" onClick={loadTraining}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!training) {
    return (
      <div className="training-container">
        <div className="training-error">
          <p className="training-error-message">Training not found</p>
          <button className="btn btn-ghost" onClick={onBack}>
            Back to Trainings
          </button>
        </div>
      </div>
    );
  }

  // Show quiz if user clicked "Start Quiz"
  if (showQuiz && training.hasQuiz) {
    return (
      <Quiz
        trainingUuid={training.uuid}
        sessionId={session?.sessionId}
        onComplete={handleQuizComplete}
        onBack={() => setShowQuiz(false)}
      />
    );
  }

  const isTrainingComplete = session?.status === "completed";
  const hasQuiz = training.hasQuiz;

  return (
    <div className="training-container">
      <div
        className={`training-detail${isMobile ? " training-detail-mobile" : ""}`}
      >
        {/* ── Back button ─────────────────────────────────────────────────── */}
        {isMobile ? (
          /* Mobile: sticky top bar with back arrow + condensed meta */
          <div className="training-detail-mobile-topbar">
            <button
              className="btn btn-ghost training-mobile-back-btn"
              onClick={onBack}
            >
              ← Back
            </button>
            <div className="training-detail-mobile-meta-inline">
              <span className="training-card-category">
                {formatCategoryForDisplay(
                  training.category
                    ? training.category.split(",")[0].trim()
                    : "General",
                )}
              </span>
              <span>{training.estimatedMinutes || 10} min</span>
              <span>{training.difficultyLabel || "Beginner"}</span>
              {training.hasQuiz && <span>Quiz</span>}
            </div>
          </div>
        ) : (
          /* Desktop: inline back button */
          <button className="btn btn-ghost training-back-btn" onClick={onBack}>
            ← Back to Trainings
          </button>
        )}

        {/* ── Header ──────────────────────────────────────────────────────── */}
        {isMobile ? (
          /* Mobile: title + summary only (meta is shown in the top bar) */
          <div className="training-detail-header training-detail-header-mobile">
            <h1 className="training-detail-title">{training.title}</h1>
            <p className="training-detail-summary">
              {training.summary || training.description}
            </p>
          </div>
        ) : (
          /* Desktop: meta row + title + summary */
          <div className="training-detail-header">
            <div className="training-detail-meta">
              <span className="training-card-category">
                {formatCategoryForDisplay(
                  training.category
                    ? training.category.split(",")[0].trim()
                    : "General",
                )}
              </span>
              <span>{training.estimatedMinutes || 10} min</span>
              <span>{training.difficultyLabel || "Beginner"}</span>
              {training.hasQuiz && <span>Quiz included</span>}
            </div>
            <h1 className="training-detail-title">{training.title}</h1>
            <p className="training-detail-summary">
              {training.summary || training.description}
            </p>
          </div>
        )}

        {/* Status Badge */}
        {!isTrainingComplete && session && (
          <div className="training-status-banner">
            <span className="training-status-banner-text">
              {hasQuiz
                ? "Read the content below, then take the quiz to complete this training."
                : "Read the content below, then click 'Mark Complete' when finished."}
            </span>
          </div>
        )}

        {/* Completion Banner */}
        {isTrainingComplete && (
          <div className="training-completion-banner">
            <h3 className="completion-banner-title">Training Complete!</h3>
            {hasQuiz && session?.quizScore != null ? (
              <p className="completion-banner-subtitle">
                You scored {session.quizScore}% on the quiz
              </p>
            ) : (
              <p className="completion-banner-subtitle">
                Great job completing this training!
              </p>
            )}
          </div>
        )}

        {/* Learning Objectives */}
        {training.learningObjectives &&
          training.learningObjectives.length > 0 && (
            <div className="training-objectives">
              <h3 className="training-objectives-title">What You'll Learn</h3>
              <ul className="training-objectives-list">
                {training.learningObjectives.map((obj, idx) => (
                  <li key={idx}>{obj}</li>
                ))}
              </ul>
            </div>
          )}

        {/* Main Content */}
        <div className="training-content">
          {training.contentBody ? (
            <div dangerouslySetInnerHTML={{ __html: training.contentBody }} />
          ) : (
            <div>
              <p>No content available for this training.</p>
            </div>
          )}
        </div>

        {/* ── Action buttons ───────────────────────────────────────────────── */}

        {/* Quiz Section - for trainings WITH quizzes */}
        {hasQuiz && !isTrainingComplete && (
          <div
            className={`quiz-section${isMobile ? " quiz-section-mobile" : ""}`}
          >
            <div className="quiz-header">
              <h2>Ready to Test Your Knowledge?</h2>
              <p className="quiz-instructions">
                Take the quiz below to complete this training and earn your
                certificate.
              </p>
              <div className="quiz-meta">
                <span>Passing score: {training.passingScore || 70}%</span>
                <span>•</span>
                <span>{training.questionCount || "5"} questions</span>
              </div>
            </div>
            <button
              className={`btn btn-primary training-quiz-start-btn${isMobile ? " training-action-btn-mobile" : ""}`}
              onClick={handleStartQuiz}
            >
              Start Quiz
            </button>
          </div>
        )}

        {/* Mark Complete Section - for trainings WITHOUT quizzes */}
        {!hasQuiz && !isTrainingComplete && (
          <div className="training-complete-section">
            <h3 className="complete-section-title">Finished Reading?</h3>
            <p className="complete-section-text">
              Click the button below to mark this training as complete.
            </p>
            <button
              className={`btn btn-primary complete-section-btn${isMobile ? " training-action-btn-mobile" : ""}`}
              onClick={handleMarkComplete}
              disabled={markingComplete}
            >
              {markingComplete ? "Saving..." : "Mark Complete"}
            </button>
          </div>
        )}

        {/* Review Quiz Button (for completed trainings with quiz) */}
        {isTrainingComplete && hasQuiz && (
          <div className="training-review-section">
            <button
              className={`btn btn-ghost${isMobile ? " training-action-btn-mobile" : ""}`}
              onClick={() => setShowQuiz(true)}
            >
              Review Quiz
            </button>
          </div>
        )}

        {/* Bottom Back Button — desktop only (mobile uses the sticky top bar) */}
        {!isMobile && (
          <div className="training-bottom-back">
            <button className="btn btn-ghost" onClick={onBack}>
              ← Back to Trainings
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingDetail;
